import { apiClient } from 'api';
import { API_ROUTES } from 'constants/routes';
import {
    EmploymentHeroBusinessOption,
    GetLocationRequestParams,
    GetLocationsRequestParams,
    ILocation,
    TandaLocationOption,
    UnleashedCustomerOption,
    UpdateLocationRequest,
    UpdateLocationRequestParams
} from 'modules/locations/types';

/**
 * Function to retrieve a list of locations from the server.
 * @param {GetLocationsRequestParams} params - Optional parameters for filtering locations by franchise partner, group, or state
 * @returns {Promise<ILocation[]>} A promise that resolves to an array of locations.
 */
export const getLocationsRequest = async (params?: GetLocationsRequestParams): Promise<ILocation[]> => {
    // Transform params to query parameters with the right format
    const queryParams: Record<string, string> = {};
    if (params?.franchise_partner_id) {
        queryParams['franchise-partner-id'] = params.franchise_partner_id;
    }
    if (params?.group_id) {
        queryParams['group-id'] = params.group_id;
    }
    if (params?.state) {
        queryParams.state = params.state;
    }

    const response = await apiClient.get<ILocation[]>(API_ROUTES.locations.getLocations, { params: queryParams });
    return response.data;
};

/**
 * Function to retrieve a list of states from the server.
 * @param params.existing When true, return only states present among current locations.
 * @returns {Promise<string[]>} A promise that resolves to an array of state names.
 */
export const getStatesRequest = async (params?: { existing?: boolean }): Promise<string[]> => {
    const response = await apiClient.get<string[]>('/locations/states', {
        params: params?.existing ? { existing: true } : undefined
    });
    return response.data;
};

export const updateLocationRequest = async ({
    pathParams,
    payload
}: {
    pathParams: UpdateLocationRequestParams;
    payload: UpdateLocationRequest;
}): Promise<ILocation> => {
    const response = await apiClient.put<ILocation>(API_ROUTES.locations.updateLocation(pathParams), payload);
    return response.data;
};

export const getLocationRequest = async (pathParams: GetLocationRequestParams): Promise<ILocation> => {
    const response = await apiClient.get<ILocation>(API_ROUTES.locations.getLocation(pathParams));
    return response.data;
};

export const getLocationEmploymentHeroBusinessesRequest = async (
    pathParams: GetLocationRequestParams
): Promise<EmploymentHeroBusinessOption[]> => {
    const response = await apiClient.get<EmploymentHeroBusinessOption[]>(
        API_ROUTES.locations.integrationEmploymentHeroBusinesses(pathParams)
    );
    return response.data;
};

export const getLocationTandaLocationsRequest = async (pathParams: GetLocationRequestParams): Promise<TandaLocationOption[]> => {
    const response = await apiClient.get<TandaLocationOption[]>(API_ROUTES.locations.integrationTandaLocations(pathParams));
    return response.data;
};

export const getLocationUnleashedCustomersRequest = async (pathParams: GetLocationRequestParams): Promise<UnleashedCustomerOption[]> => {
    const response = await apiClient.get<UnleashedCustomerOption[]>(API_ROUTES.locations.integrationUnleashedCustomers(pathParams));
    return response.data;
};
